import { Component, computed, inject, input, OnInit, output } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FormSelectComponent } from '@shared/forms';
import { TierPricingEditorComponent, CustomPricing, PricingEntity, TierPricing, createTierFormArray, createTierGroup, tierGroupToValue } from '../../shared';

interface EntityOption {
    label: string;
    value: string;
}

@Component({
    selector: 'app-custom-pricing-dialog',
    templateUrl: './custom-pricing-dialog.component.html',
    styleUrls: ['./custom-pricing-dialog.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, DialogModule, FormSelectComponent, TierPricingEditorComponent],
})
export class CustomPricingDialogComponent implements OnInit {
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly translateService = inject(TranslateService);

    readonly availableEntities = input.required<PricingEntity[]>();
    readonly defaultTiers = input.required<TierPricing[]>();
    readonly editing = input<CustomPricing | null>(null);

    readonly saveCustom = output<CustomPricing>();
    readonly cancelDialog = output<void>();

    readonly entityIdControl = this.formBuilder.control('', [FormValidators.Required]);
    readonly tierFormArray = createTierFormArray();

    readonly title = computed(() =>
        this.editing()
            ? this.translateService.instant('packages.pricing.custom.dialog.editTitle')
            : this.translateService.instant('packages.pricing.custom.dialog.addTitle'),
    );

    readonly entityOptions = computed<EntityOption[]>(() => {
        const schoolLabel = this.translateService.instant('packages.pricing.entity.school');
        const orgLabel = this.translateService.instant('packages.pricing.entity.organisation');
        return this.availableEntities().map((entity) => ({
            label: `[${entity.type === 'school' ? schoolLabel : orgLabel}] ${entity.name}`,
            value: entity.id,
        }));
    });

    ngOnInit() {
        const pricing = this.editing()?.pricingList ?? this.defaultTiers();

        for (const tier of pricing) {
            this.tierFormArray.push(createTierGroup(tier));
        }

        this.entityIdControl.setValue(this.editing()?.relatedEntityId ?? '', { emitEvent: false });
    }

    onSave(): void {
        this.entityIdControl.markAsTouched();
        this.tierFormArray.markAllAsTouched();

        if (this.entityIdControl.invalid || this.tierFormArray.invalid) {
            return;
        }

        const entityId = this.entityIdControl.value;
        const entity = this.availableEntities().find((candidate) => candidate.id === entityId);
        if (!entity) {
            return;
        }

        const pricingList = this.tierFormArray.controls.map(tierGroupToValue);

        this.saveCustom.emit({
            relatedEntityType: entity.type,
            relatedEntityId: entity.id,
            pricingList,
        });
    }

    onCancel(): void {
        this.cancelDialog.emit();
    }
}
